/**
 * Браузерная сторона web-push: одна реализация подписки на оба фронта.
 *
 * Витрина (`id.iec.exchange`) и страница входа (`auth.iec.exchange`) — разные
 * origin, поэтому подписка у каждой своя: браузер выдаёт разрешение и эндпоинт
 * на домен, а не на аккаунт. Общий здесь КОД, а не подписка — две копии этой
 * логики разошлись бы в конвертации ключа, и один из фронтов молча подписывался
 * бы тем, чем сервер не подписывает.
 *
 * Файл браузерный (`navigator`, `Notification`, DOM-типы) и потому исключён из
 * компиляции самого пакета — его собирает потребитель своим tsconfig, ровно как
 * `observability/hawk-browser.ts`.
 */

/** То, что уходит в `notifications.registerPush`. */
export interface WebPushSubscriptionPayload {
	readonly endpoint: string;
	readonly p256dh: string;
	readonly auth: string;
	readonly userAgent: string;
}

/**
 * Исход попытки подписаться. Именно перечисление, а не `null`: «браузер не
 * умеет», «человек отказал» и «не получилось» требуют разных слов на экране,
 * и склеивать их в одно значение — способ показать неправильную подсказку.
 */
export type WebPushEnrollment =
	| { readonly ok: true; readonly subscription: WebPushSubscriptionPayload }
	| { readonly ok: false; readonly reason: "unsupported" | "denied" | "failed" };

export function isWebPushSupported(): boolean {
	return (
		typeof window !== "undefined" &&
		"serviceWorker" in navigator &&
		"PushManager" in window &&
		"Notification" in window
	);
}

/** Разрешение браузера. `unsupported` — спрашивать не у чего. */
export function webPushPermission(): NotificationPermission | "unsupported" {
	return isWebPushSupported() ? Notification.permission : "unsupported";
}

/**
 * Подписать этот браузер: разрешение → регистрация воркера → подписка.
 *
 * Уже существующая подписка переиспользуется (`getSubscription`), а не
 * создаётся второй: второй эндпоинт того же браузера дал бы два одинаковых
 * уведомления на одно событие.
 */
export async function enrollInWebPush({
	serviceWorkerUrl,
	publicKey,
}: {
	serviceWorkerUrl: string;
	publicKey: string;
}): Promise<WebPushEnrollment> {
	if (!isWebPushSupported()) return { ok: false, reason: "unsupported" };

	try {
		const permission = await Notification.requestPermission();
		if (permission !== "granted") return { ok: false, reason: "denied" };

		const registration = await navigator.serviceWorker.register(serviceWorkerUrl);
		await navigator.serviceWorker.ready;
		const subscription =
			(await registration.pushManager.getSubscription()) ??
			(await registration.pushManager.subscribe({
				userVisibleOnly: true,
				applicationServerKey: base64UrlToArrayBuffer(publicKey),
			}));

		return {
			ok: true,
			subscription: {
				endpoint: subscription.endpoint,
				p256dh: arrayBufferToBase64Url(subscription.getKey("p256dh")),
				auth: arrayBufferToBase64Url(subscription.getKey("auth")),
				userAgent: navigator.userAgent,
			},
		};
	} catch {
		return { ok: false, reason: "failed" };
	}
}

/** Подписка ЭТОГО браузера, если она есть. Нужна, чтобы её снять. */
export async function currentWebPushSubscription(
	serviceWorkerUrl: string,
): Promise<PushSubscription | null> {
	if (!isWebPushSupported()) return null;
	const registration = await navigator.serviceWorker.getRegistration(serviceWorkerUrl);
	return (await registration?.pushManager.getSubscription()) ?? null;
}

/**
 * Возвращает именно `ArrayBuffer`, а не `Uint8Array`: в актуальных типах DOM
 * `applicationServerKey` объявлен как `BufferSource`, и типизированный массив
 * с обобщённым `ArrayBufferLike` под него не подходит.
 */
function base64UrlToArrayBuffer(value: string): ArrayBuffer {
	const padding = "=".repeat((4 - (value.length % 4)) % 4);
	const base64 = (value + padding).replaceAll("-", "+").replaceAll("_", "/");
	const raw = window.atob(base64);
	const buffer = new ArrayBuffer(raw.length);
	const view = new Uint8Array(buffer);
	for (let index = 0; index < raw.length; index += 1) view[index] = raw.charCodeAt(index);
	return buffer;
}

function arrayBufferToBase64Url(buffer: ArrayBuffer | null): string {
	if (!buffer) return "";
	const bytes = new Uint8Array(buffer);
	let binary = "";
	for (const byte of bytes) binary += String.fromCharCode(byte);
	return window.btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}
